// Display-size (UI zoom) presets.
//
// The root div already carries a base CSS `zoom` of 1.08 (≈ +1pt on the common
// 12–13px text, applied on every theme). The display size multiplies that base:
// Auto (the default) picks a preset from the window size; a stored fixed size is
// used as-is. Scale 1.0 leaves the root at exactly 1.08 — what the visual-snapshot
// suite pins.
//
// No pan/hit-testing math changes at any scale: `clientToLocal` / `clientToLocalX`
// in lib/timeline/geometry.js derive the effective zoom dynamically (bounding-rect
// ÷ offsetWidth), so they self-correct for whatever the root `zoom` happens to be.

export const ROOT_ZOOM_BASE = 1.08;

// Ordered smallest→largest, evenly spaced from 0.92 to 1.3. 1.0 — the historical root of
// exactly 1.08 — is no longer a preset, but stays a valid value (the visual-snapshot suite
// pins it).
export const DISPLAY_SCALE_PRESETS = [
  { scale: 0.92, label: "XS" },
  { scale: 1.015, label: "S" },
  { scale: 1.11, label: "M" },
  { scale: 1.205, label: "L" },
  { scale: 1.3, label: "XL" },
];

/** The stored setting that picks a preset from the screen size. */
export const AUTO_DISPLAY_SCALE = "auto";

/**
 * Smallest window width (CSS px) for each preset, in preset order. The first is always met.
 * Full-screen: a 13–15" laptop (1280–1679) gets S, a 16" laptop or 1080p-class monitor M,
 * a 1440p monitor L, an ultrawide XL.
 */
const AUTO_BAND_MIN_WIDTHS = [0, 1280, 1680, 2240, 2880];

/** How far past a band edge a window must go before Auto changes size, so it never flickers. */
export const AUTO_BAND_DEAD_ZONE = 40;

/**
 * Outer window height (px) Auto keeps per unit of root zoom. 600 leaves roughly 500px of
 * layout under the browser's own toolbars — measured: a 1920×620 viewport still reads well at M.
 */
export const AUTO_MIN_HEIGHT_PER_ZOOM = 600;

const bandIndex = (width) => {
  let i = 0;
  while (i + 1 < AUTO_BAND_MIN_WIDTHS.length && width >= AUTO_BAND_MIN_WIDTHS[i + 1]) i++;
  return i;
};

const MIN_SCALE = DISPLAY_SCALE_PRESETS[0].scale;
const MAX_SCALE = DISPLAY_SCALE_PRESETS[DISPLAY_SCALE_PRESETS.length - 1].scale;

/**
 * Coerce a persisted/user value to a sane scale in the supported range.
 * Corrupt / unset input — null, undefined, empty string, or non-finite
 * (NaN / Infinity) — falls back to 1.0 (Default), NOT the Compact floor: a
 * stored `null` (JSON "null" in localStorage) means "unset → default", and must
 * not silently shrink the UI. An explicit numeric value out of range still
 * clamps to the nearest preset (e.g. 0 → 0.92, 2 → 1.3).
 * @param {unknown} scale
 * @returns {number}
 */
export function clampDisplayScale(scale) {
  // null/undefined/"" are "unset" — return Default BEFORE Number() coerces them
  // to 0, which would otherwise clamp them to the Compact floor.
  if (scale == null || scale === "") return 1.0;
  const n = Number(scale);
  if (!Number.isFinite(n)) return 1.0;
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, n));
}

/** The largest preset a window this tall can show; the held preset gets the dead zone. */
const heightCapIndex = (height, held) => {
  for (let i = DISPLAY_SCALE_PRESETS.length - 1; i > 0; i--) {
    const needed = AUTO_MIN_HEIGHT_PER_ZOOM * ROOT_ZOOM_BASE * DISPLAY_SCALE_PRESETS[i].scale;
    if (height + (i === held ? AUTO_BAND_DEAD_ZONE : 0) >= needed) return i;
  }
  return 0;
};

/**
 * The preset Auto picks for a window this size (CSS px): the width band, stepped down if the
 * window is too short for it. Unknown width → S; unknown height is ignored. Given the preset
 * it currently shows, it keeps that one until the window is `AUTO_BAND_DEAD_ZONE` past an edge.
 * @param {number} width
 * @param {number} [current]
 * @param {number} [height]
 * @returns {number}
 */
export function autoDisplayScale(width, current, height) {
  if (!Number.isFinite(width) || width <= 0) return DISPLAY_SCALE_PRESETS[1].scale;
  const held = DISPLAY_SCALE_PRESETS.findIndex((p) => p.scale === current);
  let i;
  if (held < 0) {
    i = bandIndex(width);
  } else {
    const up = bandIndex(width - AUTO_BAND_DEAD_ZONE);
    const down = bandIndex(width + AUTO_BAND_DEAD_ZONE);
    i = held < up ? up : held > down ? down : held;
  }
  if (Number.isFinite(height) && height > 0) i = Math.min(i, heightCapIndex(height, held));
  return DISPLAY_SCALE_PRESETS[i].scale;
}

/**
 * The preset nearest a scale (ties go to the smaller).
 * @param {number} scale
 * @returns {number}
 */
export function nearestPresetScale(scale) {
  return DISPLAY_SCALE_PRESETS.reduce((best, p) =>
    Math.abs(p.scale - scale) < Math.abs(best.scale - scale) ? p : best,
  ).scale;
}

/** Whether a stored setting means Auto (a missing or corrupt value counts as Auto). */
export function isAutoDisplayScale(setting) {
  return (
    setting === AUTO_DISPLAY_SCALE ||
    setting == null ||
    setting === "" ||
    !Number.isFinite(Number(setting))
  );
}

/**
 * The scale to render at for a stored setting: Auto follows the window width (`autoScale`,
 * held by the hook); anything else is clamped into range.
 * @param {unknown} setting
 * @param {number} autoScale
 * @returns {number}
 */
export function resolveDisplayScale(setting, autoScale) {
  return isAutoDisplayScale(setting) ? autoScale : clampDisplayScale(setting);
}

/**
 * The setting to start from when only the pre-Auto value is stored. That value was written on
 * first load whether or not the user chose it, so the old default (1.0) carries over as Auto;
 * any other size was a real choice and maps to its nearest preset.
 * @param {unknown} legacy
 * @returns {number|string}
 */
export function migrateLegacyDisplayScale(legacy) {
  if (legacy == null || legacy === "") return AUTO_DISPLAY_SCALE;
  const n = Number(legacy);
  if (!Number.isFinite(n) || n === 1) return AUTO_DISPLAY_SCALE;
  return nearestPresetScale(clampDisplayScale(n));
}

/**
 * The effective root CSS `zoom` for a given user scale: base 1.08 × scale.
 * @param {unknown} scale
 * @returns {number}
 */
export function rootZoomFor(scale) {
  return ROOT_ZOOM_BASE * clampDisplayScale(scale);
}
